/**
 * Purpose: Initializes and manages the core game state for a session.
 * Owns: Game creation, player management (add/remove), game start, state resets,
 *       schedule generation for the classroom progression system.
 * Notes: Redesigned for the "School Day" model. Each player gets an independent
 *        schedule of 8 class periods + Final Exam. Supports Speed Run, Honor Roll,
 *        and Pure Classroom game modes, and random or chosen schedule ordering.
 *        This is the single source of truth for game data.
 */

import type {
  GameState,
  Player,
  GameConfig,
  Turn,
  PlayerColor,
  GameMode,
  Major,
  ScheduleMode,
  ClassPeriod,
} from '../types/game';
import { PLAYER_COLORS } from '../types/game';
import { TOTAL_PERIODS } from '../config/classConfig';
import {
  generateSchedule,
  generatePureClassroomSchedule,
  createEmptyClassProgress,
} from './seatProgression';

// _______________________________________________
//              EMPTY TURN TEMPLATE
// _______________________________________________

export function createEmptyTurn(playerId: string): Turn {
  return {
    playerId,
    diceValue: null,
    question: null,
    selectedAnswer: null,
    wasCorrect: null,
    pointsEarned: 0,
  };
}

// _______________________________________________
//              PLAYER MANAGEMENT
// _______________________________________________

let nextPlayerId = 1;

/**
 * Create a new player with defaults.
 * Color auto-assigned from the palette. Schedule generated based on scheduleMode
 * and gameMode (Pure Classroom uses fixed question counts instead of seats).
 * The player starts at period 1 with empty class progress for all periods.
 */
export function createPlayer(
  name: string,
  ageGroup: Player['ageGroup'],
  tier: Player['tier'],
  existingPlayers: Player[],
  scheduleMode: ScheduleMode = 'random',
  customSchedule?: ClassPeriod[],
  gameMode: GameMode = 'speedRun',
  major: Major = 'General Ed',
): Player {
  const usedColors = new Set(existingPlayers.map((p) => p.color));
  const availableColor =
    PLAYER_COLORS.find((c) => !usedColors.has(c)) ?? PLAYER_COLORS[0];

  /* Pure Classroom uses question-count schedules; other modes use seat-based schedules */
  const schedule = customSchedule
    ?? (gameMode === 'pureClassroom'
      ? generatePureClassroomSchedule(scheduleMode === 'random')
      : generateSchedule(scheduleMode === 'random'));

  const player: Player = {
    id: `player-${nextPlayerId++}`,
    name,
    ageGroup,
    tier,
    major,
    color: availableColor,
    score: 0,
    currentPeriod: 1,
    schedule,
    classProgress: createEmptyClassProgress(TOTAL_PERIODS),
    gpa: 0,
    finished: false,
    questionsCorrect: 0,
    questionsWrong: 0,
    currentStreak: 0,
    bestStreak: 0,
  };
  return player;
}

/** Update a player's tier mid-game (parents can adjust difficulty anytime). */
export function updatePlayerTier(
  players: Player[],
  playerId: string,
  newTier: Player['tier'],
): Player[] {
  return players.map((p) =>
    p.id === playerId ? { ...p, tier: newTier } : p,
  );
}

/** Update a player's color. */
export function updatePlayerColor(
  players: Player[],
  playerId: string,
  newColor: PlayerColor,
): Player[] {
  return players.map((p) =>
    p.id === playerId ? { ...p, color: newColor } : p,
  );
}

// _______________________________________________
//              GAME STATE CREATION
// _______________________________________________

/** Create the initial game state for a new session. */
export function createInitialGameState(
  players: Player[],
  packId: string,
  gameMode: GameMode = 'speedRun',
  scheduleMode: ScheduleMode = 'random',
): GameState {
  const config: GameConfig = {
    packId,
  };

  return {
    players,
    currentPlayerIndex: 0,
    currentRound: 1,
    currentTurn: createEmptyTurn(players[0]?.id ?? ''),
    diceValue: null,
    gamePhase: 'setup',
    currentQuestion: null,
    config,
    usedQuestionIds: new Set<string>(),
    hostReaction: null,
    gameMode,
    scheduleMode,
  };
}

/** Reset all player progress for a new game with the same players and settings. */
export function resetGameForRematch(state: GameState): GameState {
  const isPureClassroom = state.gameMode === 'pureClassroom';
  const randomize = state.scheduleMode === 'random';

  const resetPlayers = state.players.map((p) => ({
    ...p,
    score: 0,
    currentPeriod: 1,
    schedule: isPureClassroom
      ? generatePureClassroomSchedule(randomize)
      : generateSchedule(randomize),
    classProgress: createEmptyClassProgress(TOTAL_PERIODS),
    gpa: 0,
    finished: false,
    questionsCorrect: 0,
    questionsWrong: 0,
    currentStreak: 0,
    bestStreak: 0,
  }));

  return {
    ...state,
    players: resetPlayers,
    currentPlayerIndex: 0,
    currentRound: 1,
    currentTurn: createEmptyTurn(resetPlayers[0]?.id ?? ''),
    diceValue: null,
    gamePhase: 'setup',
    currentQuestion: null,
    usedQuestionIds: new Set<string>(),
    hostReaction: null,
  };
}
